import * as React from 'react';
import { Badge, SpaceBetween, Spinner } from '@amzn/awsui-components-react-v3';
import { TagsList } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import {
  DatabaseInfo,
  GetCatalogRequest,
  GetCatalogResponse,
  GetDataSetResponseStructure,
} from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import {
  dataClassificationOptions,
  defaultClassificationOption,
  DZ_GLUE_DATASOURCE_ID,
  DZ_REDSHIFT_DATASOURCE_ID,
  GALAXI_DATASOURCE_ID,
  PUBLIC_DATA_CLASSIFICATION,
  PUBLIC_DATA_CLASSIFICATION_ACCURATE_FROM,
} from 'src/commons/constants';
import { SelectProps } from '@amzn/awsui-components-react-v3/polaris/select/interfaces';
import { IdInfo } from 'src/components/utils/arnUtil';
import { listCatalogs } from 'src/api/catalog';

const getClassificationColor = (level: string) => {
  switch (level) {
    case 'Public':
      return 'green';
    case 'Confidential':
      return 'severity-low';
    case 'HighlyConfidential':
      return 'severity-medium';
    case 'Restricted':
      return 'severity-high';
    case 'Critical':
      return 'severity-critical';
    default:
      return 'severity-neutral';
  }
};

export const ClassificationBadge = ({
  classification,
  disabled,
  includeClassificationPrefix = false,
}: {
  classification: string;
  disabled?: boolean;
  includeClassificationPrefix?: boolean;
}) => (
  <Badge color={disabled ? 'grey' : getClassificationColor(classification)}>
    {includeClassificationPrefix ? 'AWS classification:' : ''} {classification}
  </Badge>
);

export const findOption = (
  options: SelectProps.Option[],
  value: string,
  defaultValue?: SelectProps.Option,
): SelectProps.Option => {
  return options.find((c) => c.value == value) ?? defaultValue;
};

export const getDataClassificationOption = (dataClassification: string): SelectProps.Option => {
  return findOption(dataClassificationOptions, dataClassification, defaultClassificationOption);
};

const isValidISODate = (isoString: string) => {
  if (!isoString) return false;
  const parsedDate = Date.parse(isoString);
  return !isNaN(parsedDate);
};

export const canDisplayDataClassification = (
  dataClassification: string | undefined,
  createDate: string | undefined,
): boolean => {
  if (!dataClassification) return false;
  const isValidClassification = dataClassificationOptions.map((option) => option.value).includes(dataClassification);
  const isCreateDateValid = isValidISODate(createDate);
  const isPublicClassificationAccurate =
    isCreateDateValid && new Date(createDate) > PUBLIC_DATA_CLASSIFICATION_ACCURATE_FROM;
  return isValidClassification && (dataClassification !== PUBLIC_DATA_CLASSIFICATION || isPublicClassificationAccurate);
};

interface iDataBadges {
  database?: DatabaseInfo;
  dataset?: GetDataSetResponseStructure;
  tags?: TagsList;
  businessGlossaries?: [];
  governanceTags?: [];
  hasLineage?: boolean;
  hasDataQuality?: boolean;
}

export const DataBadges = ({
  dataset,
  tags,
  businessGlossaries,
  database,
  governanceTags,
  hasLineage,
  hasDataQuality,
}: iDataBadges) => (
  <SpaceBetween size='xxs' direction='vertical'>
    <SpaceBetween size='xs' direction='horizontal'>
      {dataset?.PII ? <Badge color='red'>PII dataset</Badge> : ''}

      {dataset?.TableState && (
        <Badge color={dataset?.TableState === 'Active' ? 'green' : 'red'}>Status: {dataset?.TableState}</Badge>
      )}

      {dataset?.DataClassification && canDisplayDataClassification(dataset?.DataClassification, dataset?.CreatedOn) && (
        <ClassificationBadge classification={dataset?.DataClassification} includeClassificationPrefix={true} />
      )}

      {businessGlossaries?.map((businessGlossary) => {
        return <Badge color='green'>{businessGlossary['GlossaryValue']}</Badge>;
      })}

      {(dataset?.DataSourceType == GALAXI_DATASOURCE_ID || database?.DataSourceId == GALAXI_DATASOURCE_ID) && (
        <Badge color='blue'>Galaxi</Badge>
      )}

      {(dataset?.DataSourceType == DZ_REDSHIFT_DATASOURCE_ID ||
        database?.DataSourceId == DZ_REDSHIFT_DATASOURCE_ID ||
        dataset?.DataSourceType == DZ_GLUE_DATASOURCE_ID ||
        database?.DataSourceId == DZ_GLUE_DATASOURCE_ID) && <Badge color='grey'>DataZone</Badge>}

      {tags?.map((tag, index) => {
        return (
          <Badge key={index} color='blue'>
            {tag.tagKey}.{tag.tagValue}{' '}
          </Badge>
        );
      })}
    </SpaceBetween>
    <SpaceBetween size='xs' direction='horizontal'>
      {governanceTags === undefined && <Spinner size='normal' />}
      {governanceTags?.map((governanceTag) => (
        <Badge color='grey'>Governance tag: {governanceTag['GlossaryValue']}</Badge>
      ))}
    </SpaceBetween>
    <SpaceBetween size='xs' direction='horizontal'>
      {!!hasLineage && hasLineage && <Badge color={'green'}>Data lineage</Badge>}
      {!!hasDataQuality && hasDataQuality && <Badge color={'green'}>Data quality</Badge>}
    </SpaceBetween>
  </SpaceBetween>
);

export const getCatalogForIdInfo = async (idInfo: IdInfo) => {
  const getCatalogRequest: GetCatalogRequest = {
    Filter: {
      CatalogKeyList: [
        {
          DataSourceId: idInfo.dataSource,
          CatalogId: idInfo.catalogId,
          ClusterIdentifier: idInfo.clusterId,
          RedshiftWorkgroupName: idInfo.redshiftWorkgroupName,
          BucketName: idInfo.bucketName,
          Region: idInfo.region,
        },
      ],
    },
  };
  try {
    const getCatalogResponse: GetCatalogResponse = await listCatalogs(getCatalogRequest);
    if (getCatalogResponse?.CatalogInfoList?.length > 0) {
      return getCatalogResponse.CatalogInfoList[0];
    }
  } catch (e) {
    console.error('Error when getting catalog: ', e);
  }
};
